import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { getAccessToken } from './auth';
import { useNpsAccess } from './useNpsAccess';
import {
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CapContactsDrawer from './CapContactsDrawer';
import NpsMemberEmailLookup from './NpsMemberEmailLookup';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

import { NPS_RESULTS_URL, broadcastNpsUnresolvedCount, updateNpsResult } from './npsApi';

const SURVEY_LABELS = {
  oxr_ops: 'OXR Ops',
  oxr_purchase: 'OXR Purchase',
  cap_ops: 'CAP Ops',
};

const isResolved = (item) => item?.resolved === 1 || item?.resolved === true || item?.resolved === '1';

const followUpKindLabel = (kind) => {
  if (!kind) return null;
  const normalized = String(kind).toLowerCase();
  if (normalized === 'positive') return 'Promoter question';
  if (normalized === 'negative') return 'Detractor question';
  return kind;
};

const npsCategory = (score) => {
  if (score == null || Number.isNaN(Number(score))) {
    return { label: 'No score', tone: 'default' };
  }
  const n = Number(score);
  if (n <= 6) return { label: 'Detractor', tone: 'detractor' };
  if (n <= 8) return { label: 'Passive', tone: 'passive' };
  return { label: 'Promoter', tone: 'promoter' };
};

const npsColors = {
  detractor: { main: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
  passive: { main: '#ed6c02', bg: '#fff8e1', border: '#ffcc02' },
  promoter: { main: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
  default: { main: '#546e7a', bg: '#eceff1', border: '#b0bec5' },
};

const formatSubmitted = (iso) => {
  if (!iso) return '—';
  try {
    return format(new Date(iso), "MMM d, yyyy 'at' h:mm a");
  } catch {
    return iso;
  }
};

const TextBlock = ({ title, children }) => {
  if (!children || !String(children).trim()) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
};

const NpsResultCard = ({ item, actionBusy, onRequestMarkComplete, onOpenDetailsDialog, onOpenCapDrawer }) => {
  const category = npsCategory(item.nps_score);
  const colors = npsColors[category.tone];
  const surveyLabel = SURVEY_LABELS[item.survey_type] || item.survey_type || 'Survey';
  const name = [item.fname, item.lname].filter(Boolean).join(' ').trim() || 'Unknown respondent';

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderLeft: `4px solid ${colors.main}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: colors.bg,
              border: `1px solid ${colors.border}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="h5" fontWeight={700} lineHeight={1} sx={{ color: colors.main }}>
              {item.nps_score ?? '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.main, fontSize: '0.65rem', fontWeight: 600 }}>
              NPS
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {name}
            </Typography>
            {item.email && (
              <NpsMemberEmailLookup email={item.email} onOpenCapDrawer={onOpenCapDrawer} />
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Submitted {formatSubmitted(item.submitted_at)}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ justifyContent: 'flex-end' }}>
          <Chip size="small" label={surveyLabel} variant="outlined" />
          <Chip size="small" label={category.label} sx={{ bgcolor: colors.bg, color: colors.main, borderColor: colors.border }} />
          {isResolved(item) ? (
            <Chip size="small" label="Resolved" color="success" variant="outlined" />
          ) : (
            <Chip size="small" label="Open" color="warning" variant="filled" />
          )}
        </Stack>
      </Box>

      <Divider sx={{ my: 0.5 }} />

      <TextBlock title="Why this score?" children={item.reason_text} />
      <TextBlock title="What could we improve?" children={item.improve_text} />

      {item.follow_up_text && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: '#f5f7fa',
            border: '1px solid #cfd8dc',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: '#546e7a' }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Survey response
            </Typography>
            {followUpKindLabel(item.follow_up_kind) && (
              <Chip
                size="small"
                label={followUpKindLabel(item.follow_up_kind)}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', borderColor: '#b0bec5' }}
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
            {item.follow_up_text}
          </Typography>
        </Box>
      )}

      {Array.isArray(item.purchase_drivers) && item.purchase_drivers.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Purchase drivers
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
            {item.purchase_drivers.map((driver) => (
              <Chip key={driver} label={driver} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}

      {isResolved(item) && !item.resolution && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <TaskAltIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Marked complete
            {(item.resolved_by || item.resolved_at) && (
              <>
                {' · '}
                {[item.resolved_by, item.resolved_at ? formatSubmitted(item.resolved_at) : null]
                  .filter(Boolean)
                  .join(' · ')}
              </>
            )}
          </Typography>
        </Box>
      )}

      {isResolved(item) && item.resolution && (
        <Box
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: 1.5,
            bgcolor: '#e8f5e9',
            border: '2px solid #2e7d32',
            boxShadow: 'inset 4px 0 0 #1b5e20',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <TaskAltIcon sx={{ fontSize: 18, color: '#1b5e20' }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1b5e20' }}>
              Team resolution
            </Typography>
          </Box>
          {(item.resolved_by || item.resolved_at) && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {[item.resolved_by, item.resolved_at ? formatSubmitted(item.resolved_at) : null].filter(Boolean).join(' · ')}
            </Typography>
          )}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
            {item.resolution}
          </Typography>
        </Box>
      )}

      {!isResolved(item) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={actionBusy}
            onClick={() => onRequestMarkComplete(item)}
          >
            Mark complete
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={actionBusy}
            onClick={() => onOpenDetailsDialog(item)}
          >
            Resolve with details
          </Button>
        </Box>
      )}

      <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
        ID {item.id} · Form {item.form_id}
      </Typography>
    </Paper>
  );
};

const NpsPage = () => {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const { allowed, checked } = useNpsAccess();

  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [completeConfirmItem, setCompleteConfirmItem] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);
  const [detailsText, setDetailsText] = useState('');
  const [capDrawerRecord, setCapDrawerRecord] = useState(null);

  const loadMoreRef = useRef(null);
  const fetchingRef = useRef(false);

  const listTotal = unresolvedOnly ? unresolvedCount : total;
  const hasMore = results.length < listTotal;

  const applyResolvedItem = useCallback(
    (updated) => {
      setUnresolvedCount((prev) => {
        const next = Math.max(0, prev - 1);
        broadcastNpsUnresolvedCount(next);
        return next;
      });

      if (unresolvedOnly) {
        setResults((prev) => prev.filter((row) => row.id !== updated.id));
      } else {
        setResults((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      }
    },
    [unresolvedOnly]
  );

  const handleConfirmMarkComplete = async () => {
    if (!completeConfirmItem) return;
    const id = completeConfirmItem.id;
    setActionBusyId(id);
    setActionError(null);
    try {
      const updated = await updateNpsResult(id, { resolved: 1, resolution: null });
      applyResolvedItem(updated);
      setCompleteConfirmItem(null);
    } catch (err) {
      setActionError(err.message || 'Failed to mark complete');
    } finally {
      setActionBusyId(null);
    }
  };

  const handleOpenDetailsDialog = (item) => {
    setActionError(null);
    setDetailsItem(item);
    setDetailsText('');
  };

  const handleCloseDetailsDialog = () => {
    if (actionBusyId) return;
    setDetailsItem(null);
    setDetailsText('');
  };

  const handleSubmitDetailsResolve = async () => {
    if (!detailsItem) return;
    const resolution = detailsText.trim();
    if (!resolution) return;

    const id = detailsItem.id;
    setActionBusyId(id);
    setActionError(null);
    try {
      const updated = await updateNpsResult(id, { resolved: 1, resolution });
      applyResolvedItem(updated);
      setDetailsItem(null);
      setDetailsText('');
    } catch (err) {
      setActionError(err.message || 'Failed to save resolution');
    } finally {
      setActionBusyId(null);
    }
  };

  const fetchPage = useCallback(
    async (pageNum, { append, unresolvedFilter }) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setError(null);
      }

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Could not get access token');

        const params = new URLSearchParams({ page: String(pageNum) });
        if (unresolvedFilter) params.set('unresolved', 'true');

        const response = await fetch(`${NPS_RESULTS_URL}?${params}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.message || 'Failed to load NPS results');
        }

        const data = await response.json();
        const batch = data.results || [];

        setTotal(data.total ?? 0);
        const unresolved = data.unresolved ?? 0;
        setUnresolvedCount(unresolved);
        broadcastNpsUnresolvedCount(unresolved);
        setPage(data.page ?? pageNum);

        setResults((prev) => (append ? [...prev, ...batch] : batch));
      } catch (err) {
        setError(err.message || 'Failed to load NPS results');
        if (!append) setResults([]);
      } finally {
        fetchingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    if (accounts.length === 0) {
      navigate('/');
    }
  }, [accounts, navigate]);

  useEffect(() => {
    if (!checked || !allowed) return;
    setResults([]);
    setPage(1);
    fetchPage(1, { append: false, unresolvedFilter: unresolvedOnly });
  }, [checked, allowed, unresolvedOnly, fetchPage]);

  useEffect(() => {
    if (!allowed || loading || loadingMore || !hasMore) return;

    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !fetchingRef.current) {
          fetchPage(page + 1, { append: true, unresolvedFilter: unresolvedOnly });
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [allowed, loading, loadingMore, hasMore, page, unresolvedOnly, fetchPage]);

  if (!checked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return <NavigateToHub />;
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', pb: 6 }}>
      <CapContactsDrawer
        open={Boolean(capDrawerRecord)}
        record={capDrawerRecord}
        onClose={() => setCapDrawerRecord(null)}
      />
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <RateReviewIcon />
              <Typography variant="h5" component="h1" fontWeight={600}>
                NPS Feedback
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Review member survey responses and mark them complete or resolve with notes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Paper
              elevation={0}
              sx={{ px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 1.5, textAlign: 'center', minWidth: 88 }}
            >
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {unresolvedCount}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Open</Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{ px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 1.5, textAlign: 'center', minWidth: 88 }}
            >
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {total}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Total</Typography>
            </Paper>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={unresolvedOnly}
              onChange={(e) => setUnresolvedOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Unresolved only"
        />
        {!loading && (
          <Typography variant="body2" color="text.secondary">
            Showing {results.length} of {listTotal} response{listTotal === 1 ? '' : 's'}
          </Typography>
        )}
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {!loading && !error && results.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {unresolvedOnly ? 'No open NPS responses right now.' : 'No NPS responses found.'}
          </Typography>
        </Paper>
      )}

      {!loading && results.length > 0 && (
        <Stack spacing={2}>
          {results.map((item) => (
            <NpsResultCard
              key={item.id}
              item={item}
              actionBusy={actionBusyId === item.id}
              onRequestMarkComplete={setCompleteConfirmItem}
              onOpenDetailsDialog={handleOpenDetailsDialog}
              onOpenCapDrawer={setCapDrawerRecord}
            />
          ))}
        </Stack>
      )}

      {loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && hasMore && <Box ref={loadMoreRef} sx={{ height: 24 }} aria-hidden />}

      <Dialog
        open={Boolean(completeConfirmItem)}
        onClose={() => !actionBusyId && setCompleteConfirmItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark complete?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Close this response with no resolution notes — for feedback that needs no further follow-up.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteConfirmItem(null)} disabled={Boolean(actionBusyId)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmMarkComplete}
            disabled={Boolean(actionBusyId)}
          >
            {actionBusyId ? 'Saving…' : 'Mark complete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detailsItem)}
        onClose={handleCloseDetailsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resolve with details</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Describe what was done so the team has a record — especially when a high score still needed a follow-up.
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            minRows={4}
            fullWidth
            label="Resolution notes"
            placeholder="What we did: called member, explained billing, …"
            value={detailsText}
            onChange={(e) => setDetailsText(e.target.value)}
            disabled={Boolean(actionBusyId)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog} disabled={Boolean(actionBusyId)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitDetailsResolve}
            disabled={Boolean(actionBusyId) || !detailsText.trim()}
          >
            {actionBusyId ? 'Saving…' : 'Save & resolve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function NavigateToHub() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/global-search', { replace: true });
  }, [navigate]);
  return null;
}

export default NpsPage;
